/**
 * Compares two values for sorting, handling null/undefined and type normalization.
 * Null/undefined values sort to the end.
 *
 * @param {*} val1 - First value
 * @param {*} val2 - Second value
 * @returns {number} -1, 0, or 1
 * @throws {Error} When comparing values of different types (excluding null/undefined)
 */
const compareValues = (val1, val2) => {
  if (val1 == null && val2 == null) return 0
  if (val1 == null) return 1
  if (val2 == null) return -1

  let a = val1
  let b = val2

  if (typeof a === "string") a = a.toLocaleLowerCase()
  if (typeof b === "string") b = b.toLocaleLowerCase()

  const type1 = typeof a
  const type2 = typeof b
  if (type1 !== type2) {
    throw new Error(`Type mismatch: cannot compare ${type1} with ${type2}`)
  }

  if (a > b) return 1
  if (a < b) return -1
  return 0
}

/**
 * Creates a comparator function for sorting arrays of objects by one or more attributes.
 *
 * Features:
 * - Sorts by multiple attributes in order of priority (first attribute is primary sort key)
 * - Case-insensitive string comparison using toLowerCase()
 * - Null and undefined values are sorted to the end
 * - Throws error if comparing different data types (except null/undefined)
 * - Uses stable sorting (maintains relative order for equal elements)
 *
 * @param {...string} attributes - The object property names to sort by, in order of priority
 * @returns {Function} A comparator function suitable for Array.sort()
 *
 * @throws {Error} When comparing values of different types (excluding null/undefined)
 *
 * @example
 * // Sort by single attribute
 * const users = [{name: "Bob"}, {name: "Alice"}]
 * users.sort(AttributeSort("name"))
 * // Result: [{name: "Alice"}, {name: "Bob"}]
 *
 * @example
 * // Sort by multiple attributes
 * const data = [
 *   {dept: "Engineering", name: "Charlie"},
 *   {dept: "Engineering", name: "Alice"},
 *   {dept: "Marketing", name: "Bob"}
 * ]
 * data.sort(AttributeSort("dept", "name"))
 * // Result: Engineering dept sorted by name, then Marketing dept
 *
 * @example
 * // Real-world usage: Operating hours sorting
 * operatingHours.sort(AttributeSort("day_of_week", "start_time"))
 */
export const AttributeSort = (...attributes) => {
  return function (a, b) {
    for (const attribute of attributes) {
      const result = compareValues(a[attribute], b[attribute])
      if (result !== 0) return result
    }
    return 0
  }
}
